import { useState, useEffect, useRef } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Network,
  Users,
  UserCheck,
  Layers,
  Shield,
  User,
} from "lucide-react";
import { toast } from "react-hot-toast";
import GroupForm from "../../components/users/GroupForm";
import ConfirmationDialog from "../../widgets/ConfirmationModal";
import groupsService, {
  Group,
  GroupMetrics,
  CreateGroupData,
  UpdateGroupData,
  GroupListParams,
} from "../../services/groups.service";
import rolesService from "../../services/roles.service";
import Loader from "../../widgets/Loader";
import Tooltip from "../../widgets/ToolTip";
import { SearchFilter, MetricCard, InUseDialog } from "./ui";
import Can from "../../components/auth/Can";

function Groups() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [inUseInfo, setInUseInfo] = useState<{ group_name: string; message: string } | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [metrics, setMetrics] = useState<GroupMetrics | null>(null);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchFilters, setSearchFilters] = useState({
    group_name: "",
    group_id: "",
  });

  // Role Mapping Modal State
  const [showRoleMapping, setShowRoleMapping] = useState(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [hasExistingMapping, setHasExistingMapping] = useState(false); // ✅ to check if PUT needed

  const isInitialMount = useRef(true);

  // SearchFilter handles the debounce; we just refetch on filter / page change.
  useEffect(() => {
    fetchGroups();
    isInitialMount.current = false;
  }, [currentPage, searchFilters, pageSize]);

  const fetchGroups = async () => {
    try {
      setIsLoadingGroups(true);
      const params: GroupListParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(searchFilters.group_name && {
          group_name: searchFilters.group_name,
        }),
        ...(searchFilters.group_id && { group_id: searchFilters.group_id }),
      };

      const response = await groupsService.getGroups(params);
      setGroups((response?.data as any) || []);
      setMetrics(response?.metrics_data || null);
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  // ===== Fetch Roles =====
  const fetchRoles = async (searchValue = "") => {
    try {
      setIsLoadingRoles(true);
      // Pull a high limit so the picker has the full set; backend list returns
      // a flat `data: Role[]` array (the listRoles contract was flattened).
      const params: any = {
        skip: 0,
        limit: 500,
        ...(searchValue && { role_name: searchValue }),
      };
      const response = await rolesService.getRoles(params);
      const roleList = Array.isArray(response?.data)
        ? response.data
        : (response as any)?.data?.roles || [];
      setRoles(roleList);
    } catch (error: any) {
      toast.error("Failed to fetch roles");
    } finally {
      setIsLoadingRoles(false);
    }
  };

  // ===== Fetch Existing Group–Role Mapping =====
  const fetchGroupRoleMapping = async (groupKey: string) => {
    try {
      const response = await groupsService.userGroupRoleGet(groupKey);
      const mappedRoles = response?.data?.roles || [];

      if (mappedRoles.length > 0) {
        setHasExistingMapping(true);
        setSelectedRoles(mappedRoles.map((r: any) => r.role_id || r.role_key));
      } else {
        setHasExistingMapping(false);
        setSelectedRoles([]);
      }
    } catch (error) {
      console.error("Error fetching mapping:", error);
      setHasExistingMapping(false);
      setSelectedRoles([]);
    }
  };

  // ===== Handlers =====
  const handleAddGroup = () => {
    setSelectedGroup(null);
    setIsFormOpen(true);
  };

  const handleEditGroup = (group: Group) => {
    setSelectedGroup(group);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedGroup(null);
  };

  const handleFormSubmit = async (data: CreateGroupData | UpdateGroupData) => {
    setIsLoading(true);
    try {
      if (selectedGroup) {
        // Update existing group
        await groupsService.updateGroup(
          selectedGroup.group_id,
          data as UpdateGroupData,
        );
        handleCloseForm(); // Close modal immediately on success
        toast.success(`Group updated successfully!`);
        await fetchGroups(); // Refresh the list
      } else {
        // Add new group
        await groupsService.createGroup(data as CreateGroupData);
        handleCloseForm(); // Close modal immediately on success
        toast.success(`Group created successfully!`);
        await fetchGroups(); // Refresh the list
      }
    } catch (error: any) {
      console.error("Error in form submission:", error);
      if (error?.response?.data?.error?.error_description) {
        toast.error(error?.response?.data?.error?.error_description);
      } else if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error("Failed to create group");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMappingClick = async (group: Group) => {
    setSelectedGroup(group);
    setShowRoleMapping(true);
    await fetchRoles();
    await fetchGroupRoleMapping(group.group_id);
  };

  const handleRoleSearchChange = (e: any) => {
    const value = e.target.value;
    setRoleSearch(value);
    const debounceTimer = setTimeout(() => {
      fetchRoles(value);
    }, 400);
    return () => clearTimeout(debounceTimer);
  };

  const handleRoleToggle = (roleKey: string) => {
    setSelectedRoles((prev) =>
      prev.includes(roleKey)
        ? prev.filter((r) => r !== roleKey)
        : [...prev, roleKey],
    );
  };

  const handleSubmit = async () => {
    if (!selectedGroup) {
      toast.error("No group selected for mapping");
      return;
    }

    if (selectedRoles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }

    try {
      setIsLoading(true);
      // Prepare payload: include all selected role objects
      const selectedRoleObjects = roles.filter((role) =>
        selectedRoles.includes(role.role_key),
      );

      const payload = {
        group_key: selectedGroup.group_id,
        roles: selectedRoleObjects.map((r) => ({
          role_key: r.role_key,
          permissions: [], // include permissions if exist
        })),
      };
      if (hasExistingMapping) {
        await groupsService.userGroupRoleUpdate(payload);
        toast.success("Group–role mapping updated successfully!");
      } else {
        await groupsService.userGroupRoleMapping(payload);
        toast.success("Group–role mapping created successfully!");
      }

      handleCloseRoleMapping();
    } catch (error: any) {
      console.error("Error mapping roles to group:", error);
      // Handle API error response format
      if (error.response?.data?.error?.error_description) {
        toast.error(error.response.data.error.error_description);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to map roles to group");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseRoleMapping = () => {
    setShowRoleMapping(false);
    setSelectedRoles([]);
    setRoleSearch("");
  };

  const handleDeleteClick = (group: Group) => {
    setGroupToDelete(group);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (groupToDelete) {
      try {
        await groupsService.deleteGroup(groupToDelete?.group_id);
        setGroupToDelete(null);
        setShowDeleteConfirm(false);
        toast.success(`Group deleted successfully!`);
        await fetchGroups(); // Refresh the list
      } catch (error: any) {
        console.error("Error deleting group:", error);
        const errInfo = error?.response?.data?.error;
        // Backend returns 409 + error_code "group_in_use" when users still belong
        // to this group. Surface it as a dialog instead of a toast.
        if (errInfo?.error_code === "group_in_use") {
          const name = groupToDelete?.group_name || groupToDelete?.group_id || "this group";
          setShowDeleteConfirm(false);
          setInUseInfo({
            group_name: name,
            message: errInfo?.error_description || "This group still has members.",
          });
          setGroupToDelete(null);
        } else if (errInfo?.error_description) {
          toast.error(errInfo.error_description);
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failed to delete group");
        }
      }
    }
  };

  const handleCancelDelete = () => {
    setGroupToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const isSearching = Boolean(
    searchFilters.group_name || searchFilters.group_id,
  );
  const totalCount = isSearching ? groups.length : (metrics?.total_groups ?? 0);
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Groups</h1>
                <p className="text-xs text-slate-600">
                  Manage your user groups
                </p>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <SearchFilter
                  placeholder="Search by group name..."
                  value={searchFilters.group_name}
                  onChange={(v) => handleSearchChange("group_name", v)}
                  className="w-64"
                  debounceMs={400}
                />

                <Can permission="groups:create">
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  onClick={handleAddGroup}
                  type="button"
                >
                  <Plus className="w-4 h-4" />
                  Add Group
                </button>
                </Can>
              </div>
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <MetricCard
              icon={<Layers className="w-5 h-5" />}
              label="Total Groups"
              value={metrics?.total_groups ?? 0}
              accent="blue"
              isLoading={isLoadingGroups && !metrics}
            />
            <MetricCard
              icon={<UserCheck className="w-5 h-5" />}
              label="Groups in Use"
              value={metrics?.groups_in_use ?? 0}
              sub={
                metrics
                  ? `${Math.round(
                      metrics.total_groups
                        ? (metrics.groups_in_use / metrics.total_groups) * 100
                        : 0,
                    )}% of all groups`
                  : undefined
              }
              accent="emerald"
              isLoading={isLoadingGroups && !metrics}
            />
            <MetricCard
              icon={<Users className="w-5 h-5" />}
              label="Users with Groups"
              value={metrics?.total_users_with_groups ?? 0}
              accent="violet"
              isLoading={isLoadingGroups && !metrics}
            />
          </div>
        </div>

        {/* Groups Table */}
        <div className="pb-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-base font-semibold text-gray-900">
                Groups List
              </h3>
              <div className="text-sm text-gray-500">
                {totalCount > 0 &&
                  `Showing ${startItem}-${endItem} of ${totalCount} groups`}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Roles
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Users
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {isLoadingGroups ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <Loader />
                      </td>
                    </tr>
                  ) : groups?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        No groups found. Click "Add Group" to create your first
                        group.
                      </td>
                    </tr>
                  ) : (
                    groups?.map((group: any) => {
                      const rolesCount = group?.roles_count ?? 0;
                      const usersCount = group?.users_count ?? 0;
                      return (
                        <tr
                          key={group._id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold text-sm">
                                {(group?.group_name || group?.group_id || "?")
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {group?.group_name}
                                </div>
                                <code className="text-[11px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                                  {group?.group_id}
                                </code>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${
                                rolesCount > 0
                                  ? "bg-blue-50 text-blue-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              <Shield className="w-3 h-3" />
                              {rolesCount}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full ${
                                usersCount > 0
                                  ? "bg-violet-50 text-violet-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              <User className="w-3 h-3" />
                              {usersCount}
                            </span>
                          </td>
                          <td
                            className="px-6 py-4 text-sm text-gray-500 max-w-[320px] whitespace-normal break-words align-top"
                            title={group?.group_description || ""}
                          >
                            {group?.group_description || "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {group?.createdAt
                              ? new Date(group.createdAt).toLocaleDateString()
                              : "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-1">
                              <Can permission="groups:edit">
                              <Tooltip content="Mapping">
                                <button
                                  onClick={() => handleMappingClick(group)}
                                  className="text-emerald-600 hover:bg-emerald-50 p-2 rounded-lg transition-colors"
                                  type="button"
                                >
                                  <Network className="w-4 h-4" />
                                </button>
                              </Tooltip>
                              </Can>
                              <Can permission="groups:edit">
                              <Tooltip content="Edit">
                                <button
                                  onClick={() => handleEditGroup(group)}
                                  className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                                  type="button"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              </Tooltip>
                              </Can>
                              <Can permission="groups:delete">
                              <Tooltip content="Delete">
                                <button
                                  onClick={() => handleDeleteClick(group)}
                                  className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                  type="button"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </Tooltip>
                              </Can>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalCount > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startItem}</span> to{" "}
                    <span className="font-medium">{endItem}</span> of{" "}
                    <span className="font-medium">{totalCount}</span> results
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Show:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2)
                        pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== Role Mapping Modal ===== */}
      {showRoleMapping && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-md shadow-lg w-full max-w-lg p-6 relative">
            <button
              onClick={handleCloseRoleMapping}
              className="absolute top-6 right-6 text-gray-500 hover:text-gray-700"
            >
              <Tooltip content="Close">
                <X className="w-5 h-5" />
              </Tooltip>
            </button>
            <h2 className="text-xl font-bold text-gray-900">Role Management</h2>
            <p className="text-sm text-gray-500 mb-6">
              Manage roles for {selectedGroup?.group_name}
            </p>

            {/* Search Input */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search roles by name or key..."
                value={roleSearch}
                onChange={handleRoleSearchChange}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md 
                         focus:outline-none focus:ring-2 focus:ring-blue-500 
                         focus:border-blue-500 text-sm"
              />
            </div>

            <div className="text-sm font-semibold text-gray-700 mb-2">
              Available Roles
            </div>

            {/* Role List */}
            <div className="max-h-72 overflow-y-auto space-y-3 mb-6 pr-2">
              {isLoadingRoles ? (
                <div className="flex justify-center py-8">
                  <Loader />
                </div>
              ) : roles.length === 0 ? (
                <div className="p-4 text-center text-gray-500 border border-gray-200 rounded-md">
                  No roles found
                </div>
              ) : (
                roles.map((role: any) => {
                  const isChecked = selectedRoles.includes(role?.role_key);
                  return (
                    <div
                      key={role._id}
                      className="flex items-center p-4 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleRoleToggle(role?.role_key)}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {}} // Handled by div onClick
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                        onClick={(e) => e.stopPropagation()} // Prevent double toggle
                      />
                      <div className="ml-4 flex flex-col">
                        <span className="font-semibold text-gray-900 text-sm">
                          {role?.role_name}
                        </span>
                        <span className="text-xs text-gray-500">
                          Role Key: {role?.role_key}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">
                {selectedRoles.length} role(s) selected
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCloseRoleMapping}
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  type="button"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {isLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Group Form Modal */}
      <GroupForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        group={selectedGroup}
        onSubmit={handleFormSubmit}
        isLoading={isLoading}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Group"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        description={`Are you sure you want to delete the group <strong>${groupToDelete?.group_name}</strong>? This action cannot be undone.`}
        iconName="warning"
      />

      <InUseDialog
        isOpen={!!inUseInfo}
        onClose={() => setInUseInfo(null)}
        title={`Cannot delete "${inUseInfo?.group_name ?? ""}"`}
        message={inUseInfo?.message || ""}
        hints={[
          "Open the Users page and remove or reassign every member of this group.",
          "Then return here and try the delete again.",
        ]}
      />
    </div>
  );
}

export default Groups;
